import React, { useState } from 'react';
import {
    Box, Button, Dialog, DialogContent, Typography, Select, MenuItem,
    Checkbox, FormControlLabel, TextField, IconButton,
} from '@mui/material';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';

/**
 * "Request learning programs" — the school asks its partner to build.
 *
 * School Workspace deck slides 13-15 and Network Workspace deck slides 13-15,
 * slide code SW/SNW-P1A-Performance Summary-Take Action. P1A only.
 *
 *   13   pick objectives — "Each one becomes its own program request"
 *   14   "How should it run? N of M" — one pass per objective chosen
 *   15   "XX requests sent"
 *
 * WHY STEP 2 REPEATS. Slide 13 states that each objective becomes its own
 * request, and slide 14's counter reads "1 OF 3" with "Next program" /
 * "Previous program" controls. So the group and session format are chosen once
 * per objective, not once for the batch — three objectives means three passes,
 * and the final pass sends them all.
 *
 * THE SEVEN OBJECTIVES ARE FIXED. Ian's note on the Network deck, 25 Aug:
 * "Previously we had preselected learning objectives based on the assessment
 * results, but now we have them select from the 7 areas by default." So the list
 * is the seven COS areas every time, with nothing preselected — it does not
 * depend on the recommendation engine.
 *
 * FRONT-END ONLY. Send calls back to the caller; nothing is submitted.
 *
 * NETWORK "School(s)" IS MULTI-SELECT, EVERYTHING ELSE IS NOT. Per the
 * SNW-P1A-Performance Summary-Take Action mockup, a network can select more
 * than one school for a single program request (a real checkbox multi-select,
 * "Choose schools…" over School A-F) — one request then covers every school
 * chosen. This is scoped tightly: SCOPE_SCHOOLS ("School(s)") only ever
 * appears in NETWORK_SCOPES to begin with, so the school-workspace flow
 * (SCHOOL_SCOPES' "Whole school") is untouched by construction, and
 * Grade(s)/Class(es) stay single-select on both workspaces — no mockup shows
 * either of those as multi-select, only School(s). See isSchoolsMultiScope
 * and describeAudience below.
 */

const NAVY = '#052F44';
const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';

/**
 * Pill CTAs matching the Performance Summary page this wizard opens from.
 * One accent color throughout — orange for every primary/filled action,
 * including "Send" and "Done" (no green; this app's brand accent is orange).
 */
const primaryBtnSx = {
    textTransform: 'none', fontWeight: 700, fontSize: '13.5px',
    backgroundColor: ORANGE, color: '#fff',
    borderRadius: '50px', px: 2.5, py: 0.9, boxShadow: 'none',
    '&:hover': { backgroundColor: '#e55a00' },
};
/** Secondary/back actions — bordered, not filled, so "filled" reads as primary. */
const textBtnSx = {
    textTransform: 'none', fontWeight: 700, fontSize: '13.5px',
    color: MUTED, borderRadius: '50px', px: 2, py: 0.9,
    border: `1.5px solid ${ORANGE}`,
    '&:hover': { borderColor: '#e55a00', color: ORANGE, backgroundColor: 'rgba(255,105,0,0.04)' },
};

/** Select / TextField — a visible orange border always, darker on focus. */
const fieldSx = {
    fontSize: '14px',
    borderRadius: '10px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE, borderWidth: '1.5px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e55a00' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e55a00', borderWidth: '2px' },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#DC2626' },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
};

/** Slide 13's seven objectives, in its order. */
export const LEARNING_OBJECTIVES = [
    'Self-regulated AI-digital engagement',
    'Digital hygiene and privacy management',
    'Cyberbullying management',
    'Critical information management',
    'Authentic and ethical digital identity',
    'Content risk avoidance',
    'Contact risk avoidance',
];

/**
 * The group types, per the approved mockup.
 *
 * A network picks WHICH schools; a school has only itself, so its first
 * option is "Whole school" — an option that selects nothing further. Sending
 * a school the network wording asked it to choose from a school list it does
 * not have, which is what left the second field empty.
 */
const SCOPE_SCHOOL  = { id: 'school', label: 'Whole school', note: 'All grades and classes' };
const SCOPE_SCHOOLS = { id: 'school', label: 'School(s)',   note: 'Choose from school list' };
const SCOPE_GRADE   = { id: 'grade',  label: 'Grade(s)',    note: 'Choose from grade list' };
const SCOPE_CLASS   = { id: 'class',  label: 'Class(es)',   note: 'Choose from class list' };

const NETWORK_SCOPES = [SCOPE_SCHOOLS, SCOPE_GRADE, SCOPE_CLASS];
const SCHOOL_SCOPES  = [SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS];

/**
 * "Whole school" is the one scope that needs no second selection — it IS the
 * whole org. Every other scope needs at least one target before the request
 * can be sent. The mockup states this as
 *   ready = !!scope && (wholeSchool || targets.length > 0)
 */
const isWholeSchool = (groupType, isNetwork) => !isNetwork && groupType === SCOPE_SCHOOL.label;

/**
 * "School(s)" (SCOPE_SCHOOLS) only ever appears in NETWORK_SCOPES — a school
 * workspace's scope list is SCHOOL_SCOPES (SCOPE_SCHOOL, "Whole school",
 * instead), so this is a network-only state by construction. Per the
 * SNW-P1A-Performance Summary-Take Action mockup, a network can select MORE
 * THAN ONE school for a single program request (checkbox multi-select,
 * "Choose schools…"), unlike Grade(s)/Class(es) or the school-workspace flow,
 * which stay single-select — no mockup shows those any differently.
 */
const isSchoolsMultiScope = (groupType) => groupType === SCOPE_SCHOOLS.label;

/**
 * Human-readable audience label — shared between RunStep's live summary and
 * SentStep's confirmation cards so both describe a selection the same way.
 * Returns '' when nothing has been chosen yet; the caller substitutes its own
 * "no audience yet" / em-dash text for that case.
 *
 * Grade(s)/Class(es) on a NETWORK account get a "(network-wide)" suffix per
 * the SentStep mockup ("Grade 8 (network-wide)", "7A (network-wide)") — a
 * grade or class name alone doesn't say which of the network's schools it
 * belongs to, since (unlike a single school) grades/classes here are drawn
 * network-wide rather than from one school's own roster.
 */
const describeAudience = (detail, isNetwork) => {
    if (isSchoolsMultiScope(detail.groupType)) {
        return (detail.groupValues && detail.groupValues.length) ? detail.groupValues.join(', ') : '';
    }
    if (!detail.groupValue) return '';
    const networkWideSuffix = isNetwork && (detail.groupType === SCOPE_GRADE.label || detail.groupType === SCOPE_CLASS.label)
        ? ' (network-wide)' : '';
    return `${detail.groupValue}${networkWideSuffix}`;
};

/**
 * Every session is taught at 30-45 minutes; the school picks only how many
 * (bug sheet Ian-SW-01Oct-1 / Ian-SNW-01Oct-1). The length of a session is
 * not adjustable — its learning content is fixed — so it is printed, never
 * chosen, and no minutes are sent with the request.
 */
const SESSION_LENGTH = '30–45 minutes';

/** Slide 14's four preset formats, each with the caption printed beside it. */
const SESSION_FORMATS = [
    { label: `${SESSION_LENGTH} × 1 session`,   note: 'Single assembly or form-time slot', sessions: 1 },
    { label: `${SESSION_LENGTH} × 4 sessions`,  note: 'One half-term block',               sessions: 4 },
    { label: `${SESSION_LENGTH} × 6 sessions`,  note: 'Most requested',                    sessions: 6 },
    { label: `${SESSION_LENGTH} × 10 sessions`, note: 'Full-term program',                 sessions: 10 },
];

const SectionLabel = ({ children }) => (
    <Typography sx={{
        fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.6px',
        textTransform: 'uppercase', color: MUTED, mb: 1,
    }}>
        {children}
    </Typography>
);

/* The session carries partner_name — the partner's organization name, else the
   name they saved in Account Settings — and the caller passes this generic
   label when the partner has saved none of them. Dropped into "Your partner,
   <name>," that read "Your partner, your DQ Partner, can create…", so the
   generic label carries the sentence by itself instead. */
const GENERIC_PARTNER = 'your DQ Partner';
const hasPartnerName = (name) => Boolean(name) && name !== GENERIC_PARTNER;

/* ── Step 1 · slide 13 ────────────────────────────────────────────────────── */

const ObjectivesStep = ({ partnerName, chosen, onToggle, onCancel, onContinue }) => (
    <>
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: NAVY, letterSpacing: '-0.2px', mb: 0.5 }}>
            Request learning programs
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', color: MUTED, mb: 2.5, lineHeight: 1.5 }}>
            {hasPartnerName(partnerName) ? (
                <>Your partner, <strong style={{ color: NAVY }}>{partnerName}</strong>, can create these programs for you.</>
            ) : (
                <><strong style={{ color: NAVY }}>Your DQ Partner</strong> can create these programs for you.</>
            )}
        </Typography>

        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: NAVY }}>
            Which recommended learning objectives would you like to request?
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: MUTED, mb: 2 }}>
            Each one becomes its own program request.
        </Typography>

        {LEARNING_OBJECTIVES.map((o) => {
            const isChosen = chosen.includes(o);
            return (
                <Box
                    key={o}
                    onClick={() => onToggle(o)}
                    sx={{
                        border: `${isChosen ? 2 : 1}px solid ${isChosen ? ORANGE : BORDER}`,
                        borderRadius: '8px',
                        px: isChosen ? 1.375 : 1.5, py: isChosen ? 0.375 : 0.5, mb: 1,
                        cursor: 'pointer',
                        backgroundColor: isChosen ? 'rgba(255,105,0,0.08)' : 'transparent',
                        transition: 'border-color 120ms, background-color 120ms',
                        '&:hover': { borderColor: ORANGE },
                    }}
                >
                    <FormControlLabel
                        sx={{ width: '100%', m: 0, pointerEvents: 'none' }}
                        control={(
                            <Checkbox
                                size="small"
                                checked={isChosen}
                                onChange={() => onToggle(o)}
                                sx={{ color: BORDER, '&.Mui-checked': { color: ORANGE } }}
                            />
                        )}
                        label={<Typography sx={{ fontSize: '14px' }}>{o}</Typography>}
                    />
                </Box>
            );
        })}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '12.5px', color: MUTED }}>
                Select at least one objective
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button onClick={onCancel} sx={textBtnSx}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={chosen.length === 0}
                    onClick={onContinue}
                    sx={primaryBtnSx}
                >
                    Yes, continue
                </Button>
            </Box>
        </Box>
    </>
);

/* ── Step 2 · slide 14 ────────────────────────────────────────────────────── */

/**
 * What has to be true before a request can be sent.
 *
 * Slide 14 gates its own footer on this: the audience line reads "no
 * audience yet" until a group is chosen, and the sessions input carries a
 * printed range (1-10 sessions). A request that is
 * short an audience or carries 200 sessions reaches the partner as work they
 * have to go back and query, so it is stopped here.
 */
/**
 * @param {object}  d
 * @param {boolean} wholeSchool  Whole school covers the entire org, so it is
 *   the one scope with nothing further to choose. The mockup states the rule as
 *   ready = !!scope && (wholeSchool || targets.length > 0).
 */
const validateDetail = (d, wholeSchool = false) => {
    const errors = {};
    if (!d.groupType) errors.groupType = 'Choose a group type';
    else if (!wholeSchool) {
        if (isSchoolsMultiScope(d.groupType)) {
            if (!d.groupValues || d.groupValues.length === 0) errors.groupValue = 'Choose at least one school';
        } else if (!String(d.groupValue || '').trim()) {
            errors.groupValue = 'Choose who this is for';
        }
    }

    const sessions = Number(d.sessions);
    if (!sessions || sessions < 1 || sessions > 10) errors.sessions = '1 to 10 sessions';

    return errors;
};

/**
 * Target group — GROUP TYPE, then SELECT GRADE / CLASS (SW slide 14, the red box).
 *
 * The wizard's own fields, lifted out unchanged so the partner's "edit cohort"
 * popup (PW slide 20) shows exactly this and nothing else, as Ian asked on
 * 17 Sep. `detail` is the wizard's detail shape (groupType, groupValue,
 * groupInstituteCode, groupValues, groupInstituteCodes); `errors` comes from
 * validateDetail.
 */
export const TargetGroupFields = ({ detail, onChange, groupChoices = [], isNetwork = false, errors = {}, askForClass = false }) => {
    const wholeSchool = isWholeSchool(detail.groupType, isNetwork);
    const isSchoolsMulti = isSchoolsMultiScope(detail.groupType);
    /*
     * Grade(s) or Class(es) picked, but no class has been added yet — there is
     * nothing to choose from, so ask for a class first (LA-SNW,SW-13Oct-01).
     * Grades come from the classes, so both lists are empty together. Only
     * where the viewer owns the classes (askForClass) — the partner's Edit
     * cohort uses these fields too, and has no My Classes.
     */
    const noClassYet = askForClass && !groupChoices.length
        && (detail.groupType === SCOPE_GRADE.label || detail.groupType === SCOPE_CLASS.label);
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2.5 }}>
            <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED, mb: 0.5 }}>GROUP TYPE</Typography>
                <Select
                    size="small" fullWidth displayEmpty
                    value={detail.groupType}
                    onChange={(e) => onChange({
                        ...detail, groupType: e.target.value,
                        groupValue: '', groupInstituteCode: '',
                        groupValues: [], groupInstituteCodes: [],
                    })}
                    sx={fieldSx}
                    renderValue={(v) => v || 'Select a group type…'}
                >
                    {(isNetwork ? NETWORK_SCOPES : SCHOOL_SCOPES).map((g) => (
                        <MenuItem key={g.label} value={g.label}>
                            <Box>
                                <Typography sx={{ fontSize: '14px' }}>{g.label}</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: MUTED }}>{g.note}</Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
                {errors.groupType && (
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#DC2626', mt: 0.5 }}>{errors.groupType}</Typography>
                )}
            </Box>
            <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED, mb: 0.5 }}>
                    {isNetwork ? (isSchoolsMulti ? 'SELECT SCHOOLS' : 'SELECT SCHOOL(S)/GRADE/CLASS') : 'SELECT GRADE/CLASS'}
                </Typography>
                {/*
                  * Disabled until a group type is chosen — slide 14's own
                  * placeholder reads "Select a group type first".
                  *
                  * The options are the caller's REAL schools and classes. Slide 14
                  * lists School A…School F as checkboxes; a free-text box here let a
                  * school type anything, and the partner then received a request
                  * naming a group that does not exist.
                  */}
                {/*
                  * Whole school covers every grade and class, so there is
                  * nothing left to choose. The field stays visible for
                  * layout but says so, rather than offering an empty list
                  * that reads as a bug.
                  */}
                {isSchoolsMulti ? (
                    // Network "School(s)" — checkbox multi-select, per the
                    // SNW-P1A mockup's "Choose schools…" panel. Every other
                    // scope (Grade(s)/Class(es), and the whole school-
                    // workspace flow) keeps the single-select below.
                    <Select
                        multiple
                        size="small" fullWidth displayEmpty
                        disabled={!groupChoices.length}
                        value={detail.groupValues || []}
                        onChange={(e) => {
                            const selectedValues = e.target.value;
                            const selectedCodes = selectedValues
                                .map((v) => groupChoices.find((o) => o.value === v)?.institute_code || '')
                                .filter(Boolean);
                            onChange({
                                ...detail,
                                groupValues: selectedValues,
                                groupInstituteCodes: selectedCodes,
                            });
                        }}
                        error={!!errors.groupValue}
                        sx={fieldSx}
                        renderValue={(selected) => (selected.length ? selected.join(', ') : 'Choose schools…')}
                    >
                        {groupChoices.map((o) => (
                            <MenuItem key={`${o.value}|${o.institute_code || ''}`} value={o.value}>
                                <Checkbox
                                    size="small"
                                    checked={(detail.groupValues || []).includes(o.value)}
                                    sx={{ color: BORDER, '&.Mui-checked': { color: ORANGE }, p: '4px', mr: 0.5 }}
                                />
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    <Select
                        size="small" fullWidth displayEmpty
                        disabled={wholeSchool || !detail.groupType || !groupChoices.length}
                        value={wholeSchool ? '' : detail.groupValue}
                        onChange={(e) => {
                            const picked = groupChoices.find((o) => o.value === e.target.value);
                            onChange({
                                ...detail,
                                groupValue: e.target.value,
                                // Which school inside a network this is.
                                groupInstituteCode: picked?.institute_code || '',
                            });
                        }}
                        error={!!errors.groupValue}
                        sx={fieldSx}
                        renderValue={(v) => {
                            if (wholeSchool) return 'All grades and classes';
                            if (v) return (groupChoices.find((o) => o.value === v) || {}).label || v;
                            if (!detail.groupType) return 'Select a group type first';
                            return groupChoices.length ? 'Choose…' : 'Nothing set up yet';
                        }}
                    >
                        {groupChoices.map((o) => (
                            <MenuItem key={`${o.value}|${o.institute_code || ''}`} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                {noClassYet ? (
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#DC2626', mt: 0.5 }}>
                        You have not added a class yet. Add a class in My Classes first.
                    </Typography>
                ) : errors.groupValue && (
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#DC2626', mt: 0.5 }}>{errors.groupValue}</Typography>
                )}
            </Box>
        </Box>
    );
};

/** The scope labels and the validator, for callers of TargetGroupFields. */
export const TARGET_GROUP_SCOPES = { SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS };
export { validateDetail as validateTargetGroup };

const RunStep = ({
    objective, index, total, detail, onChange, groupChoices = [], isNetwork = false,
    onBack, onPrevious, onNext, onSend,
}) => {
    const isLast = index === total - 1;
    // Whole school needs no target, so it must not be asked for one.
    const wholeSchool = isWholeSchool(detail.groupType, isNetwork);
    const errors = validateDetail(detail, wholeSchool);
    const isValid = Object.keys(errors).length === 0;
    const audience = wholeSchool ? 'Whole school' : (describeAudience(detail, isNetwork) || 'no audience yet');

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: NAVY, letterSpacing: '-0.2px' }}>
                    How should it run?
                </Typography>
                <Typography sx={{
                    fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px',
                    color: ORANGE, backgroundColor: 'rgba(255,105,0,0.12)',
                    borderRadius: '999px', px: 1.25, py: '3px', whiteSpace: 'nowrap',
                }}>
                    {index + 1} OF {total}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.88rem', color: MUTED, mb: 2.5, lineHeight: 1.5 }}>
                Choose who it is for and the delivery format for <strong style={{ color: NAVY }}>{objective}</strong>.
            </Typography>

            <SectionLabel>Target group</SectionLabel>
            <TargetGroupFields
                detail={detail}
                onChange={onChange}
                groupChoices={groupChoices}
                isNetwork={isNetwork}
                errors={errors}
                askForClass
            />

            <SectionLabel>Session format</SectionLabel>
            {SESSION_FORMATS.map((f) => {
                const isChosen = detail.format === f.label;
                return (
                    <Box
                        key={f.label}
                        component="button"
                        type="button"
                        onClick={() => onChange({ ...detail, format: f.label, sessions: f.sessions })}
                        sx={{
                            width: '100%', textAlign: 'left', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                            border: `${isChosen ? 2 : 1}px solid ${isChosen ? ORANGE : BORDER}`,
                            backgroundColor: isChosen ? 'rgba(255,105,0,0.08)' : 'transparent',
                            borderRadius: '8px',
                            px: isChosen ? 1.875 : 2, py: isChosen ? 1.125 : 1.25, mb: 1,
                            transition: 'border-color 120ms, background-color 120ms',
                            '&:hover': { borderColor: ORANGE },
                        }}
                    >
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: isChosen ? ORANGE : NAVY }}>{f.label}</Typography>
                        <Typography sx={{ fontSize: '12.5px', color: MUTED }}>{f.note}</Typography>
                    </Box>
                );
            })}

            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', p: 2, mt: 1.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: MUTED, mb: 1.5 }}>Or enter a custom format</Typography>
                {/* Sessions only — "Minutes per session" is hidden (Ian, 16 Sep: "remove -> hide").
                    One row, "30–45 minutes  ×  [n]", with the label above the box only so the
                    three share one centre line (Ian-SNW-01Oct-1, Ian-SW-01Oct-1 — one
                    component, so both workspaces). */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', columnGap: 2, rowGap: 0.5, alignItems: 'center' }}>
                    <Box />
                    <Box />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED }}>
                        NUMBER OF SESSIONS (1-10 sessions)
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY, whiteSpace: 'nowrap' }}>{SESSION_LENGTH}</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: MUTED }}>&times;</Typography>
                    <TextField size="small" fullWidth type="number" inputProps={{ min: 1, max: 10 }}
                               value={detail.sessions}
                               error={!!errors.sessions}
                               onChange={(e) => onChange({ ...detail, sessions: e.target.value, format: '' })}
                               sx={fieldSx} />
                    <Box />
                    <Box />
                    <Typography sx={{ fontSize: '12px', color: '#d32f2f', minHeight: '18px' }}>{errors.sessions || ''}</Typography>
                </Box>
            </Box>

            {/* Anything else the build should know (LA-SW,SNW-14Oct-01) — asked
                here so it reaches the first build, not only a later "Request changes". */}
            <Box sx={{ mt: 2.5 }}>
                <SectionLabel>Any Other Information Relevant to Program Generation</SectionLabel>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={detail.additional_info || ''}
                    onChange={(e) => onChange({ ...detail, additional_info: e.target.value })}
                    placeholder="Please provide any additional information, requirements, preferences, or context that may help us tailor the program to your school's curriculum and learning needs."
                    inputProps={{ maxLength: 2000 }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '10px' } }}
                />
            </Box>

            {/*
              * The summary claims the left, the buttons are pinned right.
              *
              * space-between alone put the buttons wherever the summary left
              * them: with nothing selected the summary is short, and once the
              * row wrapped the button group became the only item on its line
              * and drifted. flex + marginLeft:auto holds them right whatever
              * the summary says.
              */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '12.5px', color: MUTED, flex: '1 1 auto', minWidth: 0 }}>
                    {audience} &middot; {SESSION_LENGTH} &times; {detail.sessions || '—'} {Number(detail.sessions) === 1 ? 'session' : 'sessions'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', flexShrink: 0 }}>
                    <Button onClick={index === 0 ? onBack : onPrevious} sx={textBtnSx}>
                        {index === 0 ? 'Back to objectives' : 'Previous program'}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isValid}
                        onClick={isLast ? onSend : onNext}
                        sx={primaryBtnSx}
                    >
                        {isLast ? `Send ${total} request${total === 1 ? '' : 's'}` : 'Next program'}
                    </Button>
                </Box>
            </Box>
        </>
    );
};

/* ── Step 3 · slide 15 ────────────────────────────────────────────────────── */

const SentStep = ({ partnerName, objectives, details, isNetwork, onDone }) => (
    <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <CheckCircleIcon style={{ color: ORANGE, fontSize: 26 }} />
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: NAVY, letterSpacing: '-0.2px' }}>
                {objectives.length} request{objectives.length === 1 ? '' : 's'} sent
            </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.88rem', color: MUTED, mb: 2.5, lineHeight: 1.5 }}>
            <strong style={{ color: NAVY }}>{hasPartnerName(partnerName) ? partnerName : 'Your DQ Partner'}</strong> has been notified by email and in their workspace.
        </Typography>

        {objectives.map((o, i) => (
            <Box key={o} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', mb: 1.5, overflow: 'hidden' }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`,
                    backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)',
                }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>
                        {i + 1} / {objectives.length}&nbsp;&nbsp;{o}
                    </Typography>
                    <Typography sx={{
                        fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px',
                        textTransform: 'uppercase', color: ORANGE,
                        backgroundColor: 'rgba(255,105,0,0.12)', borderRadius: '999px', px: 1, py: '2px',
                    }}>
                        Requested
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, p: 2 }}>
                    <Box>
                        <SectionLabel>Target group</SectionLabel>
                        {/*
                          * Whole school selects no value on purpose, so printing
                          * groupValue alone showed an em dash for the one target
                          * that is always valid. Fall back to the group type.
                          */}
                        <Typography sx={{ fontSize: '14px' }}>
                            {describeAudience(details[i] || {}, isNetwork) || details[i]?.groupType || '—'}
                        </Typography>
                    </Box>
                    <Box>
                        <SectionLabel>Session format</SectionLabel>
                        <Typography sx={{ fontSize: '14px' }}>
                            {SESSION_LENGTH} &times; {details[i]?.sessions || '—'} {Number(details[i]?.sessions) === 1 ? 'session' : 'sessions'}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        ))}

        <Box sx={{
            borderLeft: `3px solid ${ORANGE}`, borderRadius: '8px', p: 2, mt: 2,
            backgroundColor: 'var(--brand-bg, #FFFBF7)',
        }}>
            <SectionLabel>What happens next</SectionLabel>
            <Typography sx={{ fontSize: '13.5px', color: 'var(--brand-text, #111827)', lineHeight: 1.6 }}>
                You&rsquo;ll get an email when each program is ready. It will appear under{' '}
                <strong>My Programs</strong>, where you can link it to relevant classes.
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '12.5px', color: MUTED }}>
                {objectives.length} program{objectives.length === 1 ? '' : 's'} requested
            </Typography>
            <Button variant="contained" onClick={onDone} sx={{ ...primaryBtnSx, minWidth: 110 }}>
                Done
            </Button>
        </Box>
    </>
);

/* ── Shell ────────────────────────────────────────────────────────────────── */

const emptyDetail = () => ({
    groupType: '', groupValue: '', groupInstituteCode: '',
    groupValues: [], groupInstituteCodes: [],
    // No minutes: a session's length is fixed at 30-45 (SESSION_LENGTH), so the
    // request carries none and session_duration_mins is left unset.
    format: '', sessions: 5,
    additional_info: '',
});

const RequestLearningProgramsWizard = ({
    open, onClose, partnerName = 'your DQ Partner', onSend,
    schools = [], classes = [], grades = [],
    // A network chooses among its schools; a school does not.
    isNetwork = false,
}) => {
    const [step, setStep] = useState('objectives');
    const [chosen, setChosen] = useState([]);
    const [details, setDetails] = useState([]);
    const [current, setCurrent] = useState(0);

    const reset = () => { setStep('objectives'); setChosen([]); setDetails([]); setCurrent(0); };
    const close = () => { reset(); onClose && onClose(); };

    const toggle = (o) => setChosen((c) => (c.includes(o) ? c.filter((x) => x !== o) : [...c, o]));

    const startRun = () => {
        setDetails(chosen.map(() => emptyDetail()));
        setCurrent(0);
        setStep('run');
    };

    const setDetail = (d) => setDetails((all) => all.map((x, i) => (i === current ? d : x)));

    /**
     * The options behind the second field.
     *
     * Grades come from their own list. They used to be derived from class
     * names by taking the first token — on the mockup's real class names
     * ("7A", "8B") that returns the class itself, and on "Grade 6 A" it
     * returns the word "Grade" for every class in the school, collapsing the
     * whole list into one meaningless option.
     *
     * "Whole school" returns nothing on purpose: there is nothing to choose.
     */
    const choicesFor = (groupType) => {
        if (isWholeSchool(groupType, isNetwork)) return [];
        if (groupType === SCOPE_SCHOOLS.label) return schools;
        if (groupType === SCOPE_CLASS.label) return classes;
        if (groupType === SCOPE_GRADE.label) return grades;
        return [];
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '20px', backgroundColor: 'var(--brand-surface, #fff)' } }}
        >
            <DialogContent sx={{ p: 3, position: 'relative' }}>
                <IconButton
                    onClick={close}
                    aria-label="Close"
                    size="small"
                    sx={{ position: 'absolute', top: 12, right: 12, color: MUTED }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                {step === 'objectives' && (
                    <ObjectivesStep
                        partnerName={partnerName}
                        chosen={chosen}
                        onToggle={toggle}
                        onCancel={close}
                        onContinue={startRun}
                    />
                )}
                {/*
                  * Which list the second field offers, by group type. "Whole school"
                  * is the one option that needs no lookup — it is the org itself.
                  */}
                {step === 'run' && (
                    <RunStep
                        objective={chosen[current]}
                        index={current}
                        total={chosen.length}
                        detail={details[current] || emptyDetail()}
                        groupChoices={choicesFor((details[current] || emptyDetail()).groupType)}
                        isNetwork={isNetwork}
                        onChange={setDetail}
                        onBack={() => setStep('objectives')}
                        onPrevious={() => setCurrent((i) => Math.max(0, i - 1))}
                        onNext={() => setCurrent((i) => Math.min(chosen.length - 1, i + 1))}
                        onSend={() => { onSend && onSend(chosen, details); setStep('sent'); }}
                    />
                )}
                {step === 'sent' && (
                    <SentStep
                        partnerName={partnerName}
                        objectives={chosen}
                        details={details}
                        isNetwork={isNetwork}
                        onDone={close}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default RequestLearningProgramsWizard;
